// Tab.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './Tab.css';

// Icons
import Icon from 'svg/Icon';

/**
 * Tab
 *
 * Displays a single tab.
 *
 * @param {string} label - The label to display on the tab
 * @param {React.Component} icon - (Optional) Icon component to display on the tab
 * @param {string} iconKey - (Optional) Icon key to display on the tab
 * @param {string} iconResource - (Optional) Image URL/import; overrides iconKey and icon
 * @param {string} tabKey - Stable key for tab measurement
 * @param {Boolean} isActive - Whether this tab is currently active
 * @param {ReactNode} rightContent - Optional right-side content for the tab
 * @param {Function} onClick - Click handler for the tab
 * @param {Number} width - (Optional) Fixed width for the tab
 * @param {Function} tabRef - Ref callback for tab element
 *
 * @returns {ReactNode} - Tab.
 */
const Tab = React.memo(({
  label,
  icon: IconComponent,
  iconKey,
  iconResource,
  tabKey,
  isActive,
  rightContent = null,
  width,
  showLabel = true,
  onClick,
  tabRef,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- RENDER ----- //

    return (
        <div
          className={`tab-container ${isActive ? 'active' : ''}${rightContent ? ' has-right-content' : ''}`}
          style={{
            width: width ? `${width}px` : 'auto',
          }}
          ref={tabRef}
          data-tab-key={tabKey}
          onClick={onClick}
        >

            {/* BODY */}
            <div className="tab-body">
                {iconResource ? (
                    <img
                      alt=""
                      className="tab-icon-resource"
                      draggable={false}
                      src={iconResource}
                    />
                ) : iconKey ? (
                    <Icon
                      iconKey={iconKey}
                      color={theme.foreground}
                      width={18}
                      height={18}
                    />
                ) : IconComponent ? (
                    <IconComponent
                      color={theme.foreground}
                      width={18}
                      height={18}
                    />
                ) : null}

                {/* LABEL */}
                {showLabel ? <span className="tab-label">{label}</span> : null}
            </div>

            {/* RIGHT CONTENT */}
            {rightContent ? (
                <div
                  className="tab-right-content"
                  onClick={(event) => event.stopPropagation()}
                >
                    {rightContent}
                </div>
            ) : null}
        </div>
    );
});

// Dev Tools
Tab.displayName = 'Tab';

export default Tab;
